<template>
  <b-row>
    <b-col cols="12" md="4" offset-md="4">
      <b-card v-if="loading">
        <div class="d-flex align-items-center">
          <b-spinner
            small
            class="mr-1"
          />
          Loading application
        </div>
      </b-card>

      <b-card v-else-if="!error && !processed">
        <b-card-title>Application awaiting approval</b-card-title>

        <b-alert
          v-if="approvalActionError"
          variant="danger"
          show
        >
          {{ approvalActionError }}
        </b-alert>

        <p>The following new user would like access to this portal and to join your company</p>

        <div class="mb-2">
          <div class="text-muted">
            Name
          </div>
          <h4>{{ user.name }}</h4>
        </div>

        <div class="mb-2">
          <div class="text-muted">
            Email address
          </div>
          <h4>{{ user.email }}</h4>
        </div>

        <p>
          <b-button
            variant="success"
            :disabled="processing"
            @click.prevent="approveUserWaitingForApproval"
          >
            {{ processingAction === 'approve' ? 'Approving' : 'Approve' }}
          </b-button>
          <b-button
            variant="danger"
            class="ml-2"
            :disabled="processing"
            @click.prevent="rejectUserWaitingForApproval"
          >
            {{ processingAction === 'reject' ? 'Rejecting' : 'Reject' }}
          </b-button>
        </p>
        <p>
          <b-button :to="{ name: 'companyoverview' }">
            Go to company admin dashboard
          </b-button>
        </p>
      </b-card>

      <b-card v-else-if="processed">
        The application has been {{ processedAction }}.<br><br>
        <b-button @click.prevent="returnToCompanyOverview">
          Go to company admin dashboard
        </b-button>
      </b-card>

      <b-card v-else>
        {{ errorMessage || 'This application is no longer waiting for approval, or you do not have access to it.' }}<br><br>
        <b-button :to="{ name: 'companyoverview' }">
          Go to company admin dashboard
        </b-button>
      </b-card>
    </b-col>
  </b-row>
</template>

<script>
import {
  BAlert,
  BButton,
  BRow,
  BCol,
  BCard,
  BCardTitle,
  BSpinner,
} from 'bootstrap-vue'
import { mapGetters } from 'vuex'

export default {
  name: 'UserApproval',
  components: {
    BAlert,
    BButton,
    BRow,
    BCol,
    BCard,
    BCardTitle,
    BSpinner,
  },
  props: {
    userEmail: {
      type: [String],
      default: 'empty',
    },
  },
  data() {
    return {
      user: {
        name: '',
        email: '',
      },
      error: false,
      errorMessage: '',
      loading: true,
      processed: false,
      processedAction: '',
      processing: false,
      processingAction: '',
      approvalActionError: '',
    }
  },
  computed: {
    ...mapGetters('auth', [
      'isCompanyAdmin',
    ]),
  },
  created() {
    if (this.isCompanyAdmin) {
      this.getUserWaitingForApproval()
    } else {
      this.$router.push({ name: 'home' })
    }
  },
  methods: {
    getUserWaitingForApproval() {
      this.loading = true
      this.error = false
      this.errorMessage = ''
      this.approvalActionError = ''

      return this.$http.get('companymanagment.cfc?method=getUserWaitingForApproval', {
        params: { userEmail: this.userEmail },
      })
        .then(response => {
          if (response.data.status) {
            this.user = response.data.user
          } else {
            this.error = true
          }
        })
        .catch(() => {
          this.error = true
        })
        .then(() => {
          this.loading = false
        })
    },
    rejectUserWaitingForApproval() {
      return this.processUserWaitingForApproval('reject')
    },
    approveUserWaitingForApproval() {
      return this.processUserWaitingForApproval('approve')
    },
    returnToCompanyOverview() {
      this.$router.push({
        name: 'companyoverview',
        query: { refresh: String(Date.now()) },
      })
    },
    processUserWaitingForApproval(action) {
      this.processing = true
      this.processingAction = action
      this.error = false
      this.errorMessage = ''
      this.approvalActionError = ''

      const method = action === 'approve' ? 'approveUserWaitingForApproval' : 'rejectUserWaitingForApproval'

      return this.$http.post(`companymanagment.cfc?method=${method}`, new URLSearchParams({
        userEmail: this.userEmail,
      }))
        .then(response => {
          if (response.data.status) {
            this.processed = true
            this.processedAction = action === 'approve' ? 'accepted' : 'rejected'
            this.showApprovalToast(
              `Application ${this.getApprovalActionPastTense(action)}.`,
              'Success',
              'success',
            )
          } else {
            this.approvalActionError = this.getApprovalActionErrorMessage(response.data.reason)
            this.showApprovalToast(this.approvalActionError, 'Failed', 'danger')
          }
        })
        .catch(() => {
          this.approvalActionError = 'This application could not be processed. Please try again.'
          this.showApprovalToast(this.approvalActionError, 'Error', 'danger')
        })
        .then(() => {
          this.processing = false
          this.processingAction = ''
        })
    },
    getApprovalActionPastTense(action) {
      return action === 'approve' ? 'approved' : 'rejected'
    },
    getApprovalActionErrorMessage(reason) {
      return reason === 'companycap'
        ? 'This application could not be accepted because your company has reached its user limit.'
        : 'This application could not be processed. It may no longer be waiting for approval, or you may not have access to it.'
    },
    showApprovalToast(message, title, variant) {
      this.$bvToast.toast(message, {
        title,
        variant,
        solid: true,
      })
    },
  },
}
</script>
